import { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { Event } from "../../reducers/apiTypes";
import type { MapBounds } from "../../types/eventFilters";
import Marker from "./marker";
import { SidebarEventItem } from "../events/SidebarEventItem";
import { formatDateRangeShort } from "../../utils/eventDisplay";
import type { EventFilters } from "../../types/eventFilters";

mapboxgl.accessToken =
    "pk.eyJ1IjoiYXZpZ2FyZyIsImEiOiJjbW5uc2hmZHMxeW8wMndwcHF0anN2eGFiIn0.LO__WnwrTJuUua4x_xFdlw";

function boundsFromMapbox(box: mapboxgl.LngLatBounds): MapBounds {
    return {
        minLng: box.getWest(),
        minLat: box.getSouth(),
        maxLng: box.getEast(),
        maxLat: box.getNorth(),
    };
}

function boundsNearlyEqual(a: MapBounds, b: MapBounds, eps = 1e-4): boolean {
    return (
        Math.abs(a.minLng - b.minLng) < eps &&
        Math.abs(a.minLat - b.minLat) < eps &&
        Math.abs(a.maxLng - b.maxLng) < eps &&
        Math.abs(a.maxLat - b.maxLat) < eps
    );
}

function groupEventsByAddress(events: Event[]): Event[][] {
    const groups = new Map<string, Event[]>();
    for (const event of events) {
        if (event.latitude == null || event.longitude == null) continue;
        const key =
            event.address.trim() ||
            `${event.latitude},${event.longitude}`;
        const bucket = groups.get(key);
        if (bucket) bucket.push(event);
        else groups.set(key, [event]);
    }
    return [...groups.values()];
}

function markerGroupReactKey(group: Event[]): string {
    return [...group]
        .map((e) => e.id)
        .sort((a, b) => a - b)
        .join("-");
}

type MapProps = {
    events: Event[] | undefined;
    boundsFilter: MapBounds | null;
    onBoundsChange: (bounds: MapBounds) => void;
    selectedEventId: number | null;
    onSelectEvent: (event: Event) => void;
    filters: EventFilters;
};

function MapComponent({
    events,
    boundsFilter,
    onBoundsChange,
    selectedEventId,
    onSelectEvent,
    filters,
}: MapProps) {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const userMovingRef = useRef(false);
    const onBoundsChangeRef = useRef(onBoundsChange);
    onBoundsChangeRef.current = onBoundsChange;

    const eventList = events ?? [];

    useEffect(() => {
        if (!mapContainerRef.current) return;
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            center: [-122.419416, 37.774929],
            zoom: 12,
            style: "mapbox://styles/mapbox/light-v11",
        });
        mapRef.current = map;

        const pushDebounced = (b: MapBounds) => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                debounceRef.current = null;
                onBoundsChangeRef.current(b);
            }, 350);
        };

        const onMoveEnd = () => {
            const box = map.getBounds();
            if (!box) return;
            userMovingRef.current = true;
            pushDebounced(boundsFromMapbox(box));
        };

        map.on("moveend", onMoveEnd);
        map.once("load", () => {
            setMapInstance(map);
            const box = map.getBounds();
            if (box) onBoundsChangeRef.current(boundsFromMapbox(box));
        });

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            map.off("moveend", onMoveEnd);
            map.remove();
            mapRef.current = null;
            setMapInstance(null);
        };
    }, []);

    useEffect(() => {
        const map = mapInstance;
        if (!map || !boundsFilter) return;
        const cur = map.getBounds();
        if (!cur) return;
        const current = boundsFromMapbox(cur);
        if (boundsNearlyEqual(current, boundsFilter)) return;
        if (userMovingRef.current) {
            userMovingRef.current = false;
            return;
        }
        map.fitBounds(
            [
                [boundsFilter.minLng, boundsFilter.minLat],
                [boundsFilter.maxLng, boundsFilter.maxLat],
            ],
            { padding: 48, duration: 0 }
        );
    }, [boundsFilter, mapInstance]);

    useEffect(() => {
        const map = mapInstance;
        if (!map || selectedEventId == null) return;
        const event = eventList.find((e) => e.id === selectedEventId);
        if (!event?.latitude || !event?.longitude) return;
        map.flyTo({
            center: [event.longitude, event.latitude],
            zoom: 14,
            duration: 800,
        });
    }, [selectedEventId, mapInstance, eventList]);

    const dateCaption = formatDateRangeShort(filters.fromDate, filters.toDate);

    return (
        <div className="map-layout flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
            <div className="relative min-h-0 flex-1 md:min-h-0">
                <div
                    id="map-container"
                    className="absolute inset-0 z-[1] rounded-none md:rounded-2xl md:m-4 md:mr-0"
                    ref={mapContainerRef}
                />
                {eventList.length > 0 &&
                    groupEventsByAddress(eventList).map((group) => (
                        <Marker
                            key={markerGroupReactKey(group)}
                            map={mapInstance}
                            events={group}
                            selected={
                                selectedEventId != null &&
                                group.some((e) => e.id === selectedEventId)
                            }
                            onSelectEvent={onSelectEvent}
                        />
                    ))}
            </div>

            {/* Desktop sidebar */}
            <aside className="hidden min-h-0 w-80 shrink-0 flex-col overflow-hidden border-l border-border bg-background md:flex lg:w-96">
                <div className="border-b border-border px-5 py-4">
                    <h2 className="text-base font-bold text-foreground">
                        {eventList.length} events on the map
                    </h2>
                    <p className="mt-0.5 text-xs text-muted">
                        {dateCaption} · all types
                    </p>
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                    {eventList.map((event) => (
                        <SidebarEventItem
                            key={event.id}
                            event={event}
                            selected={event.id === selectedEventId}
                            onClick={() => onSelectEvent(event)}
                        />
                    ))}
                </div>
            </aside>

            {/* Mobile bottom sheet */}
            <div className="map-bottom-sheet flex min-h-0 flex-col md:hidden">
                <div className="mx-auto mb-2 mt-2 h-1 w-10 shrink-0 rounded-full bg-border" />
                <div className="shrink-0 px-4 pb-2">
                    <h2 className="text-base font-bold text-foreground">
                        {eventList.length} events nearby
                    </h2>
                </div>
                <div className="map-bottom-sheet-list flex-1 px-2 pb-4">
                    {eventList.map((event) => (
                        <SidebarEventItem
                            key={event.id}
                            event={event}
                            selected={event.id === selectedEventId}
                            onClick={() => onSelectEvent(event)}
                            compact
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default MapComponent;
